import format from 'date-fns/format'
import { Circle } from 'react-feather'
import clsx from 'clsx'
import { Domain } from '@pepper/utils/src/types/seo'
import { Tooltip, useGlobalState } from '@pepper/ui'
import { pluralize } from '@pepper/utils/src/function/pluralize'
import getProgressBarPoints from '@pepper/utils/src/function/getProgressBarPoints'

import ProjectDropdown from 'components/ProjectDropdown/ProjectDropdown'
import DraftDropdown from 'components/DraftDropdown/DraftDropdown'
import SpacedProgressBar from 'components/ProgressBar/SpacedProgressBar'
import DomainPill from 'components/DomainPill'

import s from './_.module.css'

interface ProjectCardProps {
  project: Project
  number_of_items?: number
  stage_count: { [key in AssignmentStage]?: number }
  has_pending_draft_items: boolean
  isDraft: boolean
  isPaidDraft: boolean
  domain?: Domain
  topicsCount?: number
}

const ProjectGridCard = ({
  project,
  stage_count,
  number_of_items,
  isDraft,
  isPaidDraft,
  domain,
  topicsCount,
}: ProjectCardProps): JSX.Element => {
  const {
    project_code,
    id: project_id,
    created_at: dateCreated,
    project_title,
    number_of_assignments,
  } = project
  const assignmentCounts = getProgressBarPoints(stage_count)
  const { permissions } = useGlobalState()
  const projectCode = project_code ? `#${project_code}` : ''

  const hasMarkpetplaceSubscription = permissions?.marketplace

  const showProgressBar = hasMarkpetplaceSubscription
    ? true
    : !!number_of_assignments

  return (
    <section className={s.gridCard}>
      <div className={s.gridCardHeader}>
        <div className={s.cardHeader_left}>
          <div>
            <span
              className={clsx(s.lightGray, {
                [s.hideHeader]: isDraft,
              })}
            >
              {projectCode}
            </span>
            <span className={s.gray}>
              {!isDraft && <Circle className={s.circleIcon} size={6} />}
              Created on
              <span> {format(new Date(dateCreated), 'do MMM, yyyy')}</span>
            </span>
          </div>
          <div>
            <strong
              className={clsx(s.fixWidthCard, {
                [s.fixHeight]: showProgressBar,
              })}
              title={project_title}
            >
              {project_title ?? 'N/A'}
            </strong>
          </div>
          {domain && <DomainPill domain={domain} />}
        </div>
        <button
          className={s.btnContainer}
          onClick={event => event.stopPropagation()}
        >
          {isDraft ? (
            <DraftDropdown projectId={project_id} isPaidDraft={isPaidDraft} />
          ) : (
            <ProjectDropdown
              icon="more"
              projectName={project_title}
              projectId={project_id}
              projectStatus={status}
            />
          )}
        </button>
      </div>
      {showProgressBar && (
        <footer className={s.detailsContainer}>
          <Tooltip
            placement="bottomLeft"
            overlayClassName={s.tooltip}
            title={
              !!number_of_items && (
                <div className={s.tooltipInner}>
                  <span>
                    <Circle className={s.circleGreen} size={8} />
                    Approved ({assignmentCounts.completedCount})
                  </span>
                  <span>
                    <Circle className={s.circleYellow} size={8} />
                    Approval Pending ({assignmentCounts.pendingCount})
                  </span>
                  <span>
                    <Circle className={s.circleCyan} size={8} />
                    New & In Progress ({assignmentCounts.newInProgressCount})
                  </span>
                </div>
              )
            }
          >
            <div className={s.progressContainer}>
              {number_of_items ? (
                <SpacedProgressBar count={stage_count} />
              ) : (
                <div className={s.zeroCountProgress} />
              )}
              {pluralize(number_of_items, 'Item')}
            </div>
          </Tooltip>
          {domain && <div className={s.topicsCount}>{topicsCount} Topics</div>}
        </footer>
      )}
    </section>
  )
}

export default ProjectGridCard
